import { lowerCase, startCase } from "lodash";
import { useTranslation } from "react-i18next";

import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { useGetTimeAttendanceActivityQuery } from "@/services/(company)/api.time-attendance";
import { PATHS } from "@/routes/paths";
import { cn } from "@/utils/cn";
import { useLocale } from "@/hooks/useLocale";
import {
  AvatarStack,
  HeroLine,
  PersonRow,
  RelativeTime,
  RowsSkeleton,
  WidgetEmpty,
  WidgetNoAccess,
} from "./primitives";

/**
 * Rows each size shows in the list layout. `md` is absent on purpose — it renders
 * an avatar stack instead, because one grid row cannot hold two person rows
 * without clipping them. Its query still asks for a handful so the stack has
 * more than one face in it. `grand` is `xl`'s two columns with a third row of
 * height behind them — the extra rows are where that height actually goes.
 */
const LIST_ROWS = { md: 5, lg: 4, xl: 6, grand: 10 } as const;

/**
 * Today's clock-ins and clock-outs as they happen.
 *
 * Timestamps collapse to a time-of-day for today's events and a date for older
 * ones — a feed where every row repeats the same date wastes the space it takes.
 */
export const ActivityWidget = ({ size, isEditing }: IWidgetRenderContext) => {
  const { t } = useTranslation("company");
  const { formatDate } = useLocale();
  const rows = LIST_ROWS[size as keyof typeof LIST_ROWS] ?? LIST_ROWS.md;
  const isTwoColumn = size === "xl" || size === "grand";
  const { data, isLoading, error } = useGetTimeAttendanceActivityQuery({
    limit: rows,
  });

  if (error) return <WidgetNoAccess error={error} />;

  const activities = data?.data ?? [];

  const todayStamp = formatDate(new Date(), "dd MMM yyyy");

  /**
   * At `md` the card is one row tall — 42px of body left once the shared
   * header and padding take theirs, which is a single row's worth, not two.
   * The face pile rides as `HeroLine`'s trailing accessory instead of a row
   * of its own, so "who, just now" and "who else is active" share the one
   * row the card actually has.
   */
  if (size === "md") {
    const actors = Array.from(
      new Map(
        activities.map((activity) => [
          activity.userId,
          {
            id: activity.userId,
            name: activity.fullName,
            image: activity.profileImage,
          },
        ]),
      ).values(),
    );

    const latest = activities[0];

    return (
      <div className="flex h-full items-center">
        {isLoading ? (
          <RowsSkeleton rows={1} />
        ) : !latest ? (
          <WidgetEmpty message={t("company:widgets.no_clockin_activity")} />
        ) : (
          // The most recent event as a fact, not a timestamp on its own: a
          // feed's whole value at a glance is "who, just now". No count —
          // the query is capped, so any number here would be the page size
          // wearing a total's clothes.
          <HeroLine
            avatar={{ name: latest.fullName, image: latest.profileImage }}
            primary={latest.fullName}
            caption={
              <>
                {startCase(lowerCase(latest.message))} ·{" "}
                <RelativeTime date={latest.createdAt} />
              </>
            }
            trailing={
              <AvatarStack
                people={actors}
                max={4}
                showOverflowLabel={false}
                size="sm"
                isLoading={isLoading}
              />
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-hidden">
      {isLoading ? (
        <RowsSkeleton rows={rows} />
      ) : activities.length === 0 ? (
        <WidgetEmpty message={t("company:widgets.no_clockin_activity")} />
      ) : (
        <ul
          className={cn(
            "flex flex-col",
            isTwoColumn && "grid grid-cols-2 gap-x-4",
          )}
        >
          {activities.slice(0, rows).map((activity, index) => {
            const stamp = formatDate(
              new Date(activity.createdAt),
              "dd MMM yyyy",
            );
            return (
              <li key={activity.id ?? `${activity.userId}-${index}`}>
                <PersonRow
                  name={activity.fullName}
                  image={activity.profileImage}
                  primary={activity.fullName}
                  secondary={startCase(lowerCase(activity.message))}
                  to={`${PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.VIEW(activity.userId)}?hTab=attendance-summary&duration=weekly`}
                  isEditing={isEditing}
                  trailing={
                    <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                      {stamp === todayStamp
                        ? formatDate(new Date(activity.createdAt), "h:mm a")
                        : stamp}
                    </span>
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
